/* eslint-disable import/no-extraneous-dependencies */
/* istanbul ignore file */
import { HttpErrorResponse } from '@angular/common/http';
import { createAction, props, union } from '@ngrx/store';
import {
  AcceptAuthorizationVerbiageResponseV1,
  AccountPaymentMethod,
  BankHoliday,
  CheckPaymentStatusPayload,
  ConfirmBankAuthorizationVerbiagePayload,
  ConfirmSurchargeAuthorizationVerbiagePayload,
  GetAutopayStatusResponse,
  GetBankHolidaysPayload,
  GetCutoffTimePayload,
  PaymentCompletedResponse,
  PaymentDateValidationResult,
  PaymentExecutionV1Response,
  RetrieveAuthorizationVerbiageResponseV1,
  RetrieveBankAuthorizationVerbiagePayload,
  RetrieveSurchargeAuthorizationVerbiagePayload,
  SelectedPaymentAccount,
  SerializableMap,
  TransactionAuthorizationResult,
  UpdatePaymentAccountPayload,
} from '@arc/unified-payments-frontend/shared-payment';

export const InitializePayStore = createAction(
  '[BtPay] Initialize Pay Store Action',
);

export const RetrieveBankAuthorizationVerbiageAction = createAction(
  '[BtPay] Retrieve Bank Authorization Verbiage Action',
  props<{
    payload: RetrieveBankAuthorizationVerbiagePayload;
  }>(),
);

export const ClearAuthorizationVerbiageAction = createAction(
  '[BtPay] Clear Bank Authorization Verbiage Action',
);

export const RetrieveBankAuthorizationVerbiageCompleteAction = createAction(
  '[BtPay] Retrieve Bank Authorization Verbiage Complete Action',
  props<{
    payload: RetrieveAuthorizationVerbiageResponseV1;
  }>(),
);

export const AcceptBankAuthorizationVerbiageAction = createAction(
  '[BtPay] Accept Bank Authorization Verbiage Action',
  props<{
    payload: ConfirmBankAuthorizationVerbiagePayload;
  }>(),
);

export const RetrieveSurchargeAuthorizationVerbiageAction = createAction(
  '[BtPay] Retrieve Surcharge Authorization Verbiage Action',
  props<{
    payload: RetrieveSurchargeAuthorizationVerbiagePayload;
  }>(),
);

export const RetrieveSurchargeAuthorizationVerbiageCompleteAction =
  createAction(
    '[BtPay] Retrieve Surcharge Authorization Verbiage Complete Action',
    props<{
      payload: RetrieveAuthorizationVerbiageResponseV1;
    }>(),
  );

export const AcceptSurchargeAuthorizationVerbiageAction = createAction(
  '[BtPay] Accept Surcharge Authorization Verbiage Action',
  props<{
    payload: ConfirmSurchargeAuthorizationVerbiagePayload;
  }>(),
);

export const AcceptSurchargeAuthorizationVerbiageCompleteAction = createAction(
  '[BtPay] Accept Surcharge Authorization Verbiage Complete Action',
  props<{
    payload: AcceptAuthorizationVerbiageResponseV1;
  }>(),
);

export const AcceptSurchargeAuthorizationVerbiageErrorAction = createAction(
  '[BtPay] Accept Surcharge Authorization Verbiage Error Action',
  props<{
    payload: HttpErrorResponse;
  }>(),
);

export const AuthorizePaymentAction = createAction(
  '[BtPay] Authorize Payment Action',
  props<{
    payload: {
      transactionReference: string;
    };
  }>(),
);

export const AuthorizePaymentCompleteAction = createAction(
  '[BtPay] Authorize Payment Complete Action',
  props<{
    payload: PaymentExecutionV1Response;
    transactionAuthorizationResult: TransactionAuthorizationResult;
    transactionReference: string;
  }>(),
);

export const AuthorizePaymentErrorAction = createAction(
  '[BtPay] Authorize Payment Error Action',
  props<{
    payload: TransactionAuthorizationResult;
    transactionReference: string;
  }>(),
);

export const ClearAuthorizePaymentAction = createAction(
  '[BtPay] Clear Authorize Payment Action',
);

export const CheckPaymentStatusAction = createAction(
  '[BtPay] Check Payment Status Action',
  props<{
    payload: CheckPaymentStatusPayload;
  }>(),
);

export const CheckPaymentStatusCompleteAction = createAction(
  '[BtPay] Check Payment Status Complete Action',
  props<{
    results: SerializableMap<TransactionAuthorizationResult>;
    checkStatusCount: number;
    checkStatusTimeout: boolean;
    authorizationPending: boolean;
  }>(),
);

export const CancelCheckPaymentStatusAction = createAction(
  '[BtPay] Cancel Check Payment Status Action',
);

export const CheckPaymentStatusCancelledAction = createAction(
  '[BtPay] Check Payment Status Cancelled Action',
  props<{
    results: SerializableMap<TransactionAuthorizationResult>;
  }>(),
);

export const CheckPaymentStatusErrorAction = createAction(
  '[BtPay] Check Payment Status Error Action',
  props<{
    payload: HttpErrorResponse;
  }>(),
);

export const ClearPaymentStatusAction = createAction(
  '[BtPay] Clear Payment Status Action',
);

export const ExecuteRedirectToPaymentAccountManagementAction = createAction(
  '[BtPay] Execute Redirect to Payment Account Management Action',
);

export const GetBankHolidaysAction = createAction(
  '[BtPay] Get Bank Holidays Action',
  props<{
    payload: GetBankHolidaysPayload;
  }>(),
);

export const GetBankHolidaysSuccessAction = createAction(
  '[BtPay] Get Bank Holidays Success Action',
  props<{ bankHolidays: BankHoliday[] }>(),
);

export const UpdatePaymentAccountExpirationAction = createAction(
  '[BtPay] Update Payment Account Expiration Action',
  props<{
    payload: UpdatePaymentAccountPayload;
  }>(),
);

export const UpdatePaymentAccountExpirationSuccessAction = createAction(
  '[BtPay] Update Payment Account Expiration Success Action',
);

export const UpdatePaymentAccountExpirationFailureAction = createAction(
  '[BtPay] Update Payment Account Expiration Failure Action',
);

export const PaymentCompletedAction = createAction(
  '[BtPay] Payment Completed',
  props<{
    payload: PaymentCompletedResponse;
  }>(),
);

export const GetCutoffTimeForAchAction = createAction(
  '[BtPay] Get Cutoff Time for Ach Action',
  props<{
    payload: GetCutoffTimePayload;
  }>(),
);

export const GetCutoffTimeForAchSuccessAction = createAction(
  '[BtPay] Get Cutoff Time for Ach Success Action',
  props<{ paymentCutoffTime: Date }>(),
);

export const GetCutoffTimeForAchErrorAction = createAction(
  '[BtPay] Get Cutoff Time for Ach Error Action',
  props<{
    payload: HttpErrorResponse;
  }>(),
);

export const SetPaymentAccountAction = createAction(
  '[BtPay] Set Payment Account Action',
  props<{
    paymentAccount: SelectedPaymentAccount;
  }>(),
);

export const SetPaymentDateAction = createAction(
  '[BtPay] Set Payment Date Action',
  props<{
    paymentDate: Date;
  }>(),
);

export const SetEmailRecipientsAction = createAction(
  '[BtPay] Set Email Recipients Action',
  props<{ emailRecipients: string[] }>(),
);

export const GetAutopayStatusAction = createAction(
  '[BtPay] Get Autopay Status Action',
);

export const GetAutopayStatusSuccessAction = createAction(
  '[BtPay] Get Autopay Status Success Action',
  props<{ autopayStatusResponse: GetAutopayStatusResponse }>(),
);

export const GetAutopayStatusErrorAction = createAction(
  '[BtPay] Get Autopay Status Error Action',
);

export const ValidatePaymentDateAction = createAction(
  '[BtPay] Validate Payment Date Action',
  props<{
    selectedPaymentMethodType: AccountPaymentMethod;
    originalPaymentDate: Date;
  }>(),
);

export const ValidatePaymentDateCompleteAction = createAction(
  '[BtPay] Validate Payment Date Complete Action',
  props<{
    paymentDateValidationResult: PaymentDateValidationResult;
  }>(),
);

// BTPAY-7160: the trigger is flow-specific (PoiPaymentConfirmationComponentActions,
// PoaPaymentSummaryComponentActions, PoiIntlMakePaymentComponentActions each expose their own
// checkAutopayEligibility) — each dispatched by its own container once that flow's synchronous
// eligibility conditions are met. Success/error stay shared since the resulting state
// (unenrolledAutopayAccounts) is flow-agnostic.
export const CheckAutopayEligibilitySuccessAction = createAction(
  '[Autopay Eligibility] Check Autopay Eligibility Success Action',
  props<{
    unenrolledAccounts: string[];
  }>(),
);

export const CheckAutopayEligibilityErrorAction = createAction(
  '[Autopay Eligibility] Check Autopay Eligibility Error Action',
);

const PayActions = union({
  InitializePayStore,
  UpdatePaymentAccountExpirationAction,
  UpdatePaymentAccountExpirationSuccessAction,
  UpdatePaymentAccountExpirationFailureAction,
  RetrieveBankAuthorizationVerbiageAction,
  RetrieveBankAuthorizationVerbiageCompleteAction,
  AcceptBankAuthorizationVerbiageAction,
  AuthorizePaymentAction,
  AuthorizePaymentCompleteAction,
  CheckPaymentStatusAction,
  CheckPaymentStatusCompleteAction,
  CheckPaymentStatusCancelledAction,
  CancelCheckPaymentStatusAction,
  ClearAuthorizationVerbiageAction,
  ClearAuthorizePaymentAction,
  ClearPaymentStatusAction,
  RetrieveSurchargeAuthorizationVerbiageAction,
  RetrieveSurchargeAuthorizationVerbiageCompleteAction,
  AcceptSurchargeAuthorizationVerbiageAction,
  AcceptSurchargeAuthorizationVerbiageCompleteAction,
  PaymentCompletedAction,
  GetBankHolidaysAction,
  GetBankHolidaysSuccessAction,
  GetCutoffTimeForAchAction,
  GetCutoffTimeForAchSuccessAction,
  GetCutoffTimeForAchErrorAction,
  SetPaymentAccountAction,
  SetPaymentDateAction,
  SetEmailRecipientsAction,
  GetAutopayStatusAction,
  GetAutopayStatusSuccessAction,
  GetAutopayStatusErrorAction,
  ValidatePaymentDateCompleteAction,
  CheckAutopayEligibilitySuccessAction,
  CheckAutopayEligibilityErrorAction,
});

export type PaymentActionsUnion = typeof PayActions;
